// Draws a companion from its design and a pose.
//
// Limbs come in two kinds. Stick limbs (the app companions, Clawd) are one pixel thick: arms
// stick out sideways, legs are short columns. Block limbs (Minecraft mobs) are solid rectangles
// `width` pixels thick that turn at one joint, like in Minecraft: their shape never changes,
// only their angle. Arms hang beside the body at rest, with a sleeve and a hand; legs end in a
// foot of their own color. Mobs that float have legs of style "none" whose length is how high
// they hover.
//
// The result is a list of layers drawn in order: pixels, and block limbs turned to an angle
// that isn't a right angle (those are drawn turned smoothly; see companion.js).
//
// The body is always clean: a block (Clawd) or a pixel-art drawing (app companions), resized
// by whole rows and columns, never shifted row by row. Eyes, arms, legs, outfits and things
// strapped to the back are added the same way for every design, so every move works for every
// companion. Any tilt is a smooth rotation of the finished drawing (see companion.js).
//
// Coordinates: x = 0 is the companion's center line, y = 0 the surface under its feet. Rows
// above the surface are negative.

import { clamp, hash } from './core.js';
import { mirror, linePx } from './pixels.js';
import { drawOutfit } from './outfits.js';
import { ITEMS } from './props.js';

// The size of each shape: [width, height, leg length]. A design can give its own; art bodies
// get them worked out from the size of their drawing.
export function shapesOf(design) {
  if (design.shapes) return Object.fromEntries(Object.entries(design.shapes).map(([k, [bw, bh, leg]]) => [k, { bw, bh, leg }]));
  const H = design.body.art.length, W = design.body.art[0].length, r = Math.round;
  // Legs with a length of their own (block legs, hovering) keep it; stick legs grow a little to stretch.
  const n = design.body.legs?.length, leg = n ?? 1, longer = n ?? 2;
  // Turned to the side it's squeezed a little, or drawn with its own side view if it has one.
  const side = design.body.side?.art, SW = side?.[0].length, SH = side?.length;
  return {
    normal: { bw: W, bh: H, leg }, squash: { bw: W + 1, bh: H - 1, leg }, stretch: { bw: W - 1, bh: H + 1, leg },
    tall: { bw: W - 1, bh: H + 1, leg: longer },
    side: side ? { bw: SW, bh: SH, leg } : { bw: r(W * 0.75), bh: H, leg },
    sideSquash: side ? { bw: SW + 1, bh: SH - 1, leg } : { bw: r(W * 0.8), bh: H - 1, leg },
    lean: side ? { bw: SW, bh: SH, leg: longer } : { bw: r(W * 0.7), bh: H + 1, leg: longer },
    lie: { bw: W + 1, bh: H - 2, leg: 0 },
    sideLie: side ? { bw: SW, bh: SH, leg: 0 } : { bw: W + 1, bh: H - 2, leg: 0 },   // lying in profile, legs tucked under
  };
}

// Left arm offsets from (left body column, arm row); the right arm mirrors them.
const ARM = {
  side:  [[-2, 0], [-1, 0]],
  up:    [[-1, -1], [-2, -2]],
  down:  [[-2, 1], [-1, 1]],
  head:  [[-1, -1], [-1, -2], [0, -3]],    // hand behind the head
  hold:  [[-1, 0], [-2, 0], [-3, 0]],
  reach: [[-1, -1], [-2, -1], [-3, -2]],
  none:  [],
};
const HAND = { side: [-2, 0], up: [-2, -2], down: [-2, 1], hold: [-3, 0], reach: [-3, -2], head: [0, -3], none: [-1, 0] };

const FLAME = ['#FFE38A', '#FF9B3D', '#E5482F'];
const METAL = { hi: '#E3E8EE', mid: '#B5BDC6', dark: '#8A939D', low: '#6B737C', nozzle: '#4A5058', strap: '#4A3B30' };
const WING = { V: '#BDB8D4', W: '#8E89AC', w: '#6C6789', v: '#57536F' };
const WHITE = '#FFFFFF';

export function buildCompanion(design, p, now) {
  if (p.pack === 'jetOld') return legacyJetpack(p, now);
  const side = p.view === 'side';
  const bw = Math.max(4, Math.round(p.bw)), bh = Math.max(2, Math.round(p.bh)), leg = p.leg ?? 1;
  const L0 = -Math.floor(bw / 2), R0 = L0 + bw - 1;
  const bottom = p.sit ? -1 : -(leg + 1);       // lowest body row
  const top = bottom - bh + 1;
  const out = [], under = [];
  const put = (c, r, tag) => out.push([c, r, tag]);
  const putUnder = (c, r, tag) => under.push([c, r, tag]);   // drawn behind the body
  const hatPx = new Set(), putHat = (c, r, tag) => { const q = [c, r, tag]; out.push(q); hatPx.add(q); };   // its hat goes along when its head turns
  const hand = {};
  const limbs = [];                             // block limbs, placed at the end (clothes may dress them first)

  const body = design.body.kind === 'art' ? artBody(design.body, p, bw, bh, side, now) : blockBody(p, bw, bh, side);
  for (const [c, r, tag] of body.px) put(L0 + c, top + r, tag);
  for (const [c, r, tag] of body.extra || []) put(L0 + c, top + r, tag);   // what its skin adds (a mooshroom's mushrooms)
  for (const [c, r, tag] of body.behind || []) putUnder(L0 + c, top + r, tag);   // what hangs behind it (a cape)
  const eyes = body.eyes.map(([c, r]) => [L0 + c, top + r]);
  drawEyes(body.eyeStyle, eyes, p, { put, top, bottom, L0, R0, side, def: { ...body.eyeDef, ...p.eyeColors } });
  // Arms attach at the body's edges, or `at` columns further in (so ears or a wide drawing can
  // stick out past them).
  const inset = body.armInset || 0, AL = L0 + inset, AR = R0 - inset;

  // Arms. Turned to the side, the front arm works as usual and the back arm hides behind.
  const ar = top + body.armRow, armPx = [];
  const putArm = (c, r, tag, inner) => { put(c, r, tag); armPx.push({ c, r, inner }); };
  const armL = side ? (p.pack || p.armL === 'none' ? 'none' : 'back') : p.armL;
  if (body.arms?.style === 'block') {
    // Turned, the near arm hangs over the body (where the side view says) and swings while walking,
    // more for long arms ("swing": how much more, like the Iron Golem's).
    // (The design's "at" is already in AL and AR; armLimb's own `at` is only the side view's near arm.)
    // pose.armsFront draws both arms over the body (fists brought to its chest: a gorilla pounding it).
    const a = { ...body.arms, at: undefined, L0: AL, R0: AR, ar, side, swing: side ? armSwing(p) * (body.arms.swing ?? 1) : 0, front: !!p.armsFront };
    const near = side && body.armCol !== undefined ? { at: L0 + body.armCol + (body.arms.width || 2) / 2, layer: 'front' } : {};
    for (const [which, pose] of [['L', armL], ['R', p.armR]]) {
      const limb = armLimb(pose, which, which === 'R' ? { ...a, ...near } : a);
      hand[which] = limb ? limb.hand : which === 'L' ? [AL - 1, ar] : [AR + 1, ar];
      // What it grips (a sword), or always holds when its hand is free and not folded away (its
      // design's "grip"), turns with the arm; drawn first, so the fist closes over the handle.
      // A design that carries something in each hand ("gripL" too: a spear and a shield) keeps
      // each in its own hand; "upright" keeps what it carries pointing the same way whatever the arm does.
      const holds = which === (p.holdArm || 'R'), both = !!body.arms.gripL;
      const own = both ? (which === 'L' ? body.arms.gripL : body.arms.grip) : holds && body.arms.grip;
      const grip = holds && p.grip ? p.grip : (holds && p.hold) || pose === 'crossed' ? null : own;
      // ("wristSide": turned the way it carries it in profile, a spear leaning forward past its face.)
      const ownWrist = (both && which === 'L' ? body.arms.wristL : side ? body.arms.wristSide ?? body.arms.wrist : body.arms.wrist) || 0;
      const wrist = holds && p.grip ? p.wrist || 0 : (holds && p.wrist) || ownWrist;   // "wrist" in its arms: how it carries its own item
      const upright = !!body.arms.upright && !(holds && p.grip);
      const item = limb && grip && gripLimb(limb, itemArt(design, grip), which, wrist, upright);
      if (item) limbs.push(item);
      if (limb) limbs.push(limb);
    }
  } else if (!body.arms || body.arms.style === 'none') {   // no arms: things it holds are at its sides
    hand.L = [AL - 1, ar]; hand.R = [AR + 1, ar];
  } else {
    if (armL === 'back') put(AL - 1, ar, body.darkTag);
    else (ARM[armL] || ARM.side).forEach(([dc, dr], i) => putArm(AL + dc, ar + dr, body.armTag, i === 0));
    if (p.armR === 'crossed') for (let c = AR - 3; c <= AR - 1; c++) put(c, ar, body.darkTag);   // folded across the front
    else (ARM[p.armR] || ARM.side).forEach(([dc, dr], i) => putArm(AR - dc, ar + dr, body.armTag, i === 0));
    const hl = HAND[armL] || HAND.side, hr = HAND[p.armR] || HAND.side;
    hand.L = [AL + hl[0], ar + hl[1]];
    hand.R = [AR - hr[0], ar + hr[1]];
  }

  // Legs.
  const legPx = [];
  const legArgs = { cols: body.legCols.map(c => L0 + c), bottom, leg, now, put: (c, r, tag = body.legTag) => { put(c, r, tag); legPx.push({ c, r }); } };
  if (body.legs?.style === 'block') {
    const splay = body.splay && { hipY: top + body.splay.hip + 0.5, reach: body.splay.reach };
    // Side-on, legs can have a drawing of their own ("sideArt"), both pointing the same way (an elf's curly toes forward).
    const sideArt = side && body.legs.sideArt;
    limbs.push(...legLimbs(p, { ...legArgs, side, splay, w: body.legs.width || 2, foot: body.legs.foot ? '@feet' : '@legs', footRows: body.legs.footRows || 1,
      art: sideArt || body.legs.art, alike: !!sideArt }));
  } else if (body.legs?.style !== 'none') drawLegs(p, legArgs);
  // Its tail, in the side view: a block limb behind the body (see tailLimb).
  if (body.tail) limbs.push(...tailLimb(body.tail, p, L0, top));

  // Clothes and hats. Clothes dress stick limbs pixel by pixel and block limbs by recoloring
  // their sleeves and legs (feet keep their own color).
  const dressArms = color => {
    for (const a of armPx) if (a.inner) put(a.c, a.r, color);
    for (const l of limbs) if (l.kind === 'arm') l.px = l.px.map(([j, k, t]) => [j, k, k < l.sleeve ? color : t]);
  };
  const dressLegs = color => {
    for (const q of legPx) put(q.c, q.r, color);
    for (const l of limbs) if (l.kind === 'leg') l.px = l.px.map(([j, k, t]) => [j, k, k < l.n - l.footRows ? color : t]);
  };
  // Clothes cover the body's pixels, or only the letters its design dresses ("dress": the
  // dragon's torso, not its wings or head); then those also set how wide a cape or a tie sits.
  const dress = design.body.dress && new Set(design.body.dress.map(k => '@' + k));
  const bodyPx = body.px.filter(([, , tag]) => !dress || dress.has(tag)).map(([c, r]) => [L0 + c, top + r]);
  const dressed = dress && bodyPx.length ? { L0: Math.min(...bodyPx.map(q => q[0])), R0: Math.max(...bodyPx.map(q => q[0])) } : { L0, R0 };
  const hat = body.head ? { L0: L0 + body.head.cols[0], R0: L0 + body.head.cols[1], top: top + body.head.top } : null;
  // A front drawing that faces a way ("faces") is dressed like a side view: tie in front, cape behind.
  const hatTop = drawOutfit(p.outfit, p, { put, putUnder, putHat, ...dressed, top, bottom, ar: top + body.clothesRow, leg, side: side || design.body.faces === 'right', bodyPx, armPx, legPx, eyes, hat, dressArms, dressLegs });

  // Things strapped to its back. They attach where the design says its back is (in the side
  // view) and where its torso is (in the front view); without that, at the drawing's edges.
  // Turned to the side, the back is on the left before mirroring.
  const back = body.back ? { col: L0 + body.back.col, top: top + body.back.top, bottom: top + body.back.bottom } : { col: L0, top, bottom };
  const torso = body.torso ? { l: L0 + body.torso.l, r: L0 + body.torso.r, top: top + body.torso.top, bottom: top + body.torso.bottom } : { l: L0, r: R0, top, bottom };
  if (p.pack === 'jetNew') jetpack(p, now, { L0: back.col, top: back.top, bottom: back.bottom, put });
  if (p.pack === 'elytraOpen') elytraOpen(back.col, back.top, putUnder);
  if (p.pack === 'elytraFolded') {
    if (side) {
      for (let r = back.top; r <= back.bottom; r++) putUnder(back.col - 1, r, r === back.top ? WING.V : WING.W);
      for (let r = back.top + 1; r < back.bottom; r++) putUnder(back.col - 2, r, WING.w);
    } else {
      for (let r = torso.top; r <= torso.bottom + 1; r++) {
        putUnder(torso.l - 1, r, r === torso.top ? WING.V : WING.W);
        putUnder(torso.r + 1, r, r === torso.top ? WING.V : WING.W);
      }
    }
  }

  // Its head turns at the neck (a sheep grazing): the head, with its eyes and anything on it,
  // comes off the body and turns as one block around the joint, lowered as it goes. It's drawn
  // before the limbs, so a near arm raised in front of it (a piglin admiring gold) stays in front.
  if (body.neck && p.nod) { const head = headLimb(out, hatPx, body.neck, p.nod, L0, top); if (head) limbs.unshift(head); }

  // Block limbs at right angles land on the pixel grid; the others are drawn turned. Either way
  // they're drawn in their order (a sword under the fist that grips it, an arm over the head).
  const limbLayer = limb => { const cells = exactCells(limb); return cells ? { px: cells } : { limb }; };
  let layers = [{ px: under }, ...limbs.filter(l => l.layer !== 'front').map(limbLayer),
    { px: out }, ...limbs.filter(l => l.layer === 'front').map(limbLayer)];
  // Facing left it's drawn mirrored: its side view, and a front drawing that faces right
  // ("faces": "right", a 3/4 view like the Ender Dragon's).
  if ((side || design.body.faces === 'right') && p.face < 0) {
    layers = layers.map(layer => (layer.px ? { px: mirror(layer.px) } : { limb: mirrorLimb(layer.limb) }));
    for (const k of Object.keys(hand)) hand[k] = [-1 - hand[k][0], hand[k][1]];   // R stays the front claw
  }
  // All its pixels in one list (turned limbs roughly), for its size and for where to grab it.
  const px = layers.flatMap(layer => (layer.px ? layer.px : approxCells(layer.limb)));
  return { px, layers, hand, top, bottom, L0, R0, hatTop: Math.min(top, hatTop) };
}

// ---------- bodies ----------
// A block (Clawd): rows of equal width. Top and bottom rows carry their own tags so skins can
// shade them.
function blockBody(p, bw, bh, side) {
  const px = [];
  for (let r = 0; r < bh; r++) {
    const inset = p.breath && r === 0 ? 1 : 0;           // a breath narrows the top row a little
    for (let c = inset; c < bw - inset; c++) px.push([c, r, r === 0 ? '@^body' : r === bh - 1 ? '@_body' : '@body']);
  }
  const eyeRow = bh >= 6 ? 2 : 1;
  const eyes = side ? [[3, eyeRow], [bw - 2, eyeRow]] : [[2, eyeRow], [bw - 3, eyeRow]];
  const legCols = bw >= 10 ? [1, 3, bw - 4, bw - 2] : [0, 2, bw - 3, bw - 1];
  const armRow = Math.round((bh - 1) / 2);
  return { px, eyes, eyeStyle: 'notch', armRow, clothesRow: armRow, legCols, arms: { style: 'stick' },
    armTag: '@body', legTag: '@body', darkTag: '@~body' };
}

// A drawing (app companions, mobs), resized to the shape by picking whole rows and columns.
// Turned to the side it uses its side view ("side": { "art", "eyes", "legs" }) if it has one,
// drawn facing right.
function artBody(body, p, bw, bh, side, now) {
  // A variant asked for by the pose (Foldi opening up), or the next frame of its idle animation
  // ("cycle": { "variants": ["", "flap"], "every": 120 }: wings flapping, rods turning; p.pace
  // speeds it up or slows it down). A cycle can go on under some looks ("under": ["shoot"]):
  // the cells its frames change (the Ghast's tentacles) keep moving, the rest shows the look.
  // A form is a variant it keeps on until it changes it (pose.form, which resetPose leaves alone:
  // a bat that is a vampire for a while), shown whenever no other look is asked for. Eyes drawn
  // into the picture can't open wide, so a design can have a look for that ("wide" in its eyes:
  // the Chrome dino's dead-eyed stare when it's hit).
  const shown = p.variant || p.form || (p.eyes === 'wide' && body.eyes.wide) || null;
  const under = !!shown && !!body.cycle?.under?.includes(shown);
  const cycle = (!shown || under) && body.cycle;
  const frame = cycle ? cycle.variants[Math.floor(now * (p.pace ?? 1) / cycle.every) % cycle.variants.length] : null;
  const name = shown || frame;
  const variant = name && body.variants?.[name];
  // Turned to the side, a variant comes from the side view's own variants, if it has them.
  const profile = side && body.side ? body.side : null, sideVariant = profile && name && profile.variants?.[name];
  let art = sideVariant?.art || profile?.art || variant?.art || body.art;
  if (under) art = underCycle(art, body, profile, frame);
  const eyeAt = profile ? sideVariant?.eyes || profile.eyes || [] : variant?.eyes || body.eyes.at;
  const H = art.length, W = art[0].length;
  // A drawing of its own for exactly this size, without a variant ("sized": { "10x6": { "art",
  // "eyes" } } in its body or side view: a slime's squash and stretch drawn by hand, so its face
  // stays whole), instead of the resized one. Its eyes are where it says, unscaled.
  const sized = !name && (side ? profile : body)?.sized?.[`${bw}x${bh}`];
  const src = sized ? sized.art : art, SH = src.length, SW = src[0].length;
  const blinkOff = body.blink && Math.floor(now / body.blink.period) % 2 === 1;
  const shut = (p.blink || p.eyes === 'closed' || p.eyes === 'sleep') && body.eyes.closed;   // { letter: letter } while the eyes are shut
  // Its own hat ("hat": { "letters", "head" } in its body: a snowman's top hat) comes off while it
  // wears another: those letters aren't drawn, and the other hat sits on the head "hat" marks (its
  // side view and looks can mark theirs: "hat": { "head" }).
  const hatOff = body.hat && p.outfit?.head && p.outfit.head !== 'none' ? body.hat : null;
  // Letters its moves draw as others for now (pose.swap: { letter: letter }, or a function that
  // gives one as it's drawn; resetPose leaves it alone): Byte's cells going on and off, Beep's
  // battery bar running down.
  const swap = typeof p.swap === 'function' ? p.swap() : p.swap;
  // A light of its own that flickers ("flicker": { "every": ms, "frames": [{ letter: letter }, ...] }
  // in its body: a jack-o'-lantern's candle): every `every` ms a frame picked at random draws some
  // of its letters as others, in every look (an empty frame: as drawn). pose.swap goes over it.
  const fl = body.flicker, flicker = fl && fl.frames[Math.floor(hash(Math.floor(now / fl.every), 7, 13) * fl.frames.length)];
  const px = [];
  for (let r = 0; r < bh; r++) {
    const row = src[Math.min(SH - 1, Math.floor((r + 0.5) * SH / bh))];
    for (let c = 0; c < bw; c++) {
      let ch = row[Math.min(SW - 1, Math.floor((c + 0.5) * SW / bw))];
      if (ch === '.' || ch === ' ') continue;
      if (hatOff?.letters.includes(ch)) continue;
      if (blinkOff && ch === body.blink.letter) ch = body.blink.off;
      if (shut && shut[ch]) ch = shut[ch];
      if (flicker?.[ch]) ch = flicker[ch];
      if (swap?.[ch]) ch = swap[ch];
      if (p.breath && r === 0 && (c === 0 || c === bw - 1)) continue;
      px.push([c, r, '@' + ch]);
    }
  }
  const sx = c => Math.round(c * bw / W), sy = r => Math.round(r * bh / H);
  const shift = side && !profile ? 1 : 0;                  // squeezed when turned: the face slides toward the front
  // A clock's hands on its face (not on a side drawing of its own), showing the real time.
  if (body.hands && !profile) px.push(...clockHands(body.hands, bw / body.art[0].length, bh / body.art.length, shift));
  const eyes = sized?.eyes || eyeAt.map(([c, r]) => [clamp(sx(c) + shift, 0, bw - 2), sy(r)]);
  const legCols = ((profile && profile.legs) || body.legs?.cols || []).map(c => clamp(Math.round((c + 0.5) * bw / W - 0.5), 0, bw - 1));
  const armRow = body.arms ? clamp(sy(profile?.armRow ?? body.arms.row), 0, bh - 1) : Math.round(bh / 2);
  const clothesAt = profile?.clothesRow ?? body.clothesRow;
  const clothesRow = clothesAt !== undefined ? clamp(sy(clothesAt), 0, bh - 1) : armRow;
  const eyeDef = profile?.eye ? { ...body.eyes, ...profile.eye } : body.eyes;
  const armInset = body.arms?.at ? Math.round(body.arms.at * bw / W) : 0;
  const col = c => clamp(Math.round((c + 0.5) * bw / W - 0.5), 0, bw - 1);
  const bare = hatOff && (profile ? sideVariant?.hat?.head || profile.hat?.head : variant?.hat?.head || hatOff.head);   // (its own hat off)
  const headAt = bare || (profile ? sideVariant?.head || profile.head : variant?.head || body.head);   // a look can move it (a shulker's lid)
  const head = headAt && { cols: headAt.cols.map(col), top: sy(headAt.top || 0) };
  // Where things strap on: its back in the side view, its torso in the front view.
  const back = profile?.back && { col: col(profile.back.col), top: sy(profile.back.rows[0]), bottom: sy(profile.back.rows[1]) };
  const torso = !side && body.torso && { l: col(body.torso.cols[0]), r: col(body.torso.cols[1]), top: sy(body.torso.rows[0]), bottom: sy(body.torso.rows[1]) };
  const armCol = profile?.armCol !== undefined ? col(profile.armCol) : undefined;
  // Its neck in the side view: where its head is (areas [left, right, bottom]: those columns,
  // from above the drawing down to that row), the joint the head turns around, and how far it
  // turns (degrees) and drops (rows) at a full nod.
  const nk = profile?.neck;
  const neck = nk && { areas: nk.head.map(([c0, c1, r1]) => [sx(c0), sx(c1 + 1) - 1, sy(r1 + 1) - 1]),
    pivot: [nk.pivot[0] * bw / W, nk.pivot[1] * bh / H], turn: nk.turn * Math.PI / 180, drop: nk.drop * bh / H };
  // Little drawings its skin adds ("extra": a mooshroom's mushrooms): each keeps its own shape,
  // placed where its [col, row] lands when the body is resized. Its own "behind" drawings (in its
  // body, side view or a look) are placed the same way, behind everything: a cape hanging down
  // past its legs, a halo around its head (they may start left of the drawing).
  const drawings = (list, at = col) => (list || []).flatMap(([c0, r0, rows]) => rows.flatMap((line, k) =>
    [...line].flatMap((ch, j) => (ch === '.' ? [] : [[at(c0) + j, sy(r0) + k, '@' + ch]]))));
  const extra = drawings(p.extra?.[profile ? 'side' : 'front']);
  const behind = drawings(profile ? sideVariant?.behind ?? profile.behind : variant?.behind ?? body.behind, c => Math.round((c + 0.5) * bw / W - 0.5));
  // Splayed legs (a spider's): the row of their hips, and each leg's length (see legLimbs).
  const splayAt = profile ? profile.splay : body.legs?.splay;
  const splay = splayAt && { hip: sy(splayAt.hip), reach: splayAt.reach };
  // Its tail in the side view. A side variant can move it (a dog sitting has it on the ground)
  // or leave it out ("tail": false, when its drawing has the tail in it).
  const tailAt = profile?.tail && sideVariant?.tail !== false && { ...profile.tail, ...sideVariant?.tail,
    tip: profile.tail.tip && { ...profile.tail.tip, ...sideVariant?.tail?.tip } };
  const tail = tailAt && { ...tailAt, at: [tailAt.at[0] * bw / W, tailAt.at[1] * bh / H] };
  return { px, eyes, eyeStyle: body.eyes.style || 'pupil', eyeDef, armRow, clothesRow, armInset, head, back, torso, armCol, legCols, neck, extra, behind, splay, tail,
    arms: body.arms || null, legs: body.legs, armTag: '@arms', legTag: '@legs', darkTag: '@~arms' };
}

// A clock's hands ("hands": { "at": [col, row], "letter", "hour", "minute" } in its body): they
// turn around the hub at [col, row] of its drawing with the real time of day, the hour hand
// `hour` and the minute hand `minute` of its pixels long, drawn in palette letter `letter` as
// part of the body (so its eyes and clothes go over them). kx, ky: how much the drawing is
// resized; shift: how far its face slides when it's squeezed turning. The time comes from
// Date.now() rather than new Date(), so the gallery's clock can set it.
function clockHands({ at: [c, r], letter, hour, minute }, kx, ky, shift) {
  const d = new Date(Date.now()), min = d.getMinutes() + d.getSeconds() / 60, hr = (d.getHours() % 12) + min / 60;
  const cx = (c + 0.5) * kx - 0.5 + shift, cy = (r + 0.5) * ky - 0.5, hub = [Math.round(cx), Math.round(cy)], tag = '@' + letter;
  const px = [[...hub, tag]];
  for (const [turn, length] of [[hr / 12, hour], [min / 60, minute]]) {
    const a = turn * 2 * Math.PI;
    px.push(...linePx(hub[0], hub[1], Math.round(cx + Math.sin(a) * length * kx), Math.round(cy - Math.cos(a) * length * ky), tag));
  }
  return px;
}

// A look with the moving cells of its idle animation taken from the frame (see artBody): the
// cells that differ between the cycle's frames, front or side. Worked out once per look and frame.
const cycleArt = (body, profile, name) => (profile ? profile.variants?.[name]?.art || profile.art : body.variants?.[name]?.art || body.art);
const underArts = new WeakMap();
function underCycle(art, body, profile, frame) {
  let byFrame = underArts.get(art);
  if (!byFrame) underArts.set(art, (byFrame = new Map()));
  if (!byFrame.has(frame)) {
    const frames = body.cycle.variants.map(name => cycleArt(body, profile, name)), mine = cycleArt(body, profile, frame);
    const moves = (r, c) => frames.some(f => f[r]?.[c] !== frames[0][r]?.[c]);
    byFrame.set(frame, art.map((row, r) => [...row].map((ch, c) => (moves(r, c) ? mine[r]?.[c] ?? ch : ch)).join('')));
  }
  return byFrame.get(frame);
}

// ---------- eyes ----------
// notch: Clawd's single dark pixels. pupil: a white 2 x 2 eye with a dark pupil, for bodies
// whose colors would hide a dark dot. block: a Minecraft eye, a row of `w` pixels (2 by default)
// in white with the iris at the inner end ("look": "out" puts it at the outer end, "middle" in
// the middle); looking left
// or right moves the iris, a blink closes it with the "lid" color. none: the eyes are part of
// the drawing (a skeleton's sockets, a spider's red eyes) and don't move or blink. A pupil's
// white and its pupil, and a notch, can take colors too (white, iris: glowing evil eyes); a
// notch with a "glow" color is two pixels tall, that color on top. ghost: the big eyes of
// Pac-Man's ghosts, a white 4 x 5 oval with a 2 x 2 pupil that looks where it's going, the whole
// eye moving that way too, as in the arcade; shut, a line. bead: a plush toy's shiny eye, `w` x
// `h` (2 x 3 by default) in the skin's eye color (or "iris") with a shine ("white") in its top
// corner: the whole eye moves a pixel to look around, wide it's a row taller, shut it's a line.
const GHOST_LOOK ={ left: [-1, 0, 0, 2], right: [1, 0, 2, 2], up: [0, -3, 1, 0], down: [0, 0, 1, 3], wide: [0, -1, 1, 1] };   // the eye's move, then the pupil in it
function drawEyes(style, eyes, p, { put, top, bottom, def = {} }) {
  const closed = p.blink || p.eyes === 'closed' || p.eyes === 'sleep';
  if (!eyes.length || (style === 'none' && !p.glasses)) return;
  const bw = def.w || 2;
  if (p.glasses) {
    const w = style === 'pupil' ? 2 : style === 'block' ? bw : style === 'ghost' ? 4 : 1;
    for (const [ec, er] of eyes) for (let c = ec - 1; c <= ec + w; c++) for (const r of [er, er + 1]) put(c, r, '#121212');
    if (eyes.length > 1) for (let c = eyes[0][0] + w + 1; c <= eyes[1][0] - 2; c++) put(c, eyes[0][1], '#2E2E2E');
    for (const [ec, er] of eyes) put(ec - 1, er, '#C4E8FF');
    return;
  }
  if (style === 'block') {
    const white = def.white || WHITE, iris = def.iris || '#1A1A1A';
    eyes.forEach(([ec, er], i) => {
      if (closed) { for (let dc = 0; dc < bw; dc++) put(ec + dc, er, def.lid || 'C'); return; }
      const inner = def.look === 'middle' ? Math.floor(bw / 2) : (i === 0) !== (def.look === 'out') ? bw - 1 : 0;
      const at = p.eyes === 'left' ? 0 : p.eyes === 'right' ? bw - 1 : inner;
      for (let dc = 0; dc < bw; dc++) put(ec + dc, er, dc === at ? iris : white);
    });
    return;
  }
  if (style === 'pupil') {
    eyes.forEach(([ec, er], i) => {
      if (closed) { put(ec, er + 1, 'E'); put(ec + 1, er + 1, 'E'); return; }
      for (const [dc, dr] of [[0, 0], [1, 0], [0, 1], [1, 1]]) put(ec + dc, er + dr, def.white || WHITE);
      const look = { left: [0, 1], right: [1, 1], up: [i === 0 ? 1 : 0, 0], down: [i === 0 ? 1 : 0, 1], wide: [i === 0 ? 1 : 0, 0] }[p.eyes] || [i === 0 ? 1 : 0, 1];
      put(ec + look[0], er + look[1], def.iris || 'E');
    });
    return;
  }
  if (style === 'ghost') {
    const [mx, my, px, py] = GHOST_LOOK[p.eyes] || [0, 0, 1, 2], white = def.white || WHITE;
    for (const [ec, er] of eyes) {
      if (closed) { for (let dc = 0; dc < 4; dc++) put(ec + dc, er + 3, white); continue; }
      for (let r = 0; r < 5; r++) for (let c = 0; c < 4; c++) {
        if ((r === 0 || r === 4) && (c === 0 || c === 3)) continue;              // its corners are round
        const pupil = c >= px && c < px + 2 && r >= py && r < py + 2;
        put(ec + mx + c, er + my + r, pupil ? def.iris || 'E' : white);
      }
    }
    return;
  }
  if (style === 'bead') {
    const h = def.h || 3, [dx, dy] = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] }[p.eyes] || [0, 0];
    const eye = def.iris || 'E', top0 = p.eyes === 'wide' ? -1 : 0;
    for (const [ec, er] of eyes) {
      if (closed) { for (let dc = 0; dc < bw; dc++) put(ec + dc, er + Math.floor(h / 2), eye); continue; }
      for (let r = top0; r < h; r++) for (let c = 0; c < bw; c++) put(ec + dx + c, er + dy + r, r === top0 && c === 0 ? def.white || WHITE : eye);
    }
    return;
  }
  const look = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] }[p.eyes] || [0, 0];
  const notch = def.iris || 'E';
  for (const [ec, er] of eyes) {
    if (closed) put(ec, er, 'C');
    else if (p.eyes === 'wide') { put(ec, er, notch); if (er - 1 >= top) put(ec, er - 1, def.glow || notch); }
    else {
      const c = ec + look[0], r = clamp(er + look[1], top, bottom);
      put(c, r, notch);
      if (def.glow && r - 1 >= top) put(c, r - 1, def.glow);
    }
  }
}

// ---------- legs ----------
function drawLegs(p, { cols, bottom, leg, now, put }) {
  const mode = p.legs || 'stand';
  if (mode === 'none' || leg === 0 && !p.sit) return;
  if (p.sit) {                                 // legs hang over the edge and swing now and then
    cols.forEach((c, i) => {
      put(c, 0);
      if (Math.sin(now / 520 + i * 1.9) < 0.55) put(c, 1);
    });
    return;
  }
  const half = cols.length / 2;
  let feet = cols.map(() => 0);
  if (mode === 'walkB') feet = cols.map((_, i) => (i < half ? -1 : 1));
  else if (mode === 'spread') feet = cols.map((_, i) => (i < half ? -2 + i : 1 + i - Math.ceil(half)));
  else if (mode === 'kick') feet = Math.floor(now / 90) % 2 ? cols.map((_, i) => (i < half ? -2 : 2)) : cols.map((_, i) => (i < half ? 1 : -1));
  for (let k = 1; k <= leg; k++) {
    const r = bottom + k, foot = k === leg;
    cols.forEach((c, i) => put(c + (foot ? feet[i] : 0), r));
  }
}

// ---------- block limbs ----------
// A block limb is a rectangle `w` wide and `n` long that hangs down from its pivot (its joint).
// Its pixels are listed across it (j) and along it (k), and drawn from (ox, oy) relative to the
// pivot. Angle 0 points down; positive angles turn it clockwise, which for the left arm is
// outward. At right angles it lands exactly on the pixel grid.
const QUARTER = Math.PI / 2;
// How far each arm pose turns the left arm out from hanging down; the right arm mirrors it.
const ARM_ANGLE = { side: 0, down: 0, hold: QUARTER, reach: QUARTER * 1.5, up: Math.PI, head: Math.PI * 0.85, crossed: -QUARTER,
  forward: QUARTER };   // held out in front (seen side-on; from the front only its end shows)

// The arms swing back and forth while it walks in profile, opposite to the legs.
const armSwing = p => (p.legs === 'walk' ? -0.45 * Math.sin(p.stride || 0) : p.legs === 'walkA' ? -0.35 : p.legs === 'walkB' ? 0.35 : 0);

function armLimb(pose, which, { L0, R0, ar, width: w = 2, length: n = 4, sleeve = 2, swing = 0, at, layer, side, rest, art, artR, front }) {
  if (pose === 'side' && rest) pose = rest;             // its own resting pose ("rest": "forward": a zombie's arms are held out)
  if (pose === 'none' || pose === 'back') return null;
  const left = which === 'L', crossed = pose === 'crossed';
  if (crossed) n = Math.min(n, R0 - L0 + 1 + w / 2);   // folded arms stay in front of the body
  const endOn = pose === 'forward' && !side;            // held out toward you: only the end of the hand shows
  if (endOn) { n = w; sleeve = 0; }
  // A number is an angle of its own (a sword swing). Folded arms seen side-on point forward
  // across the chest, not back past it.
  const out = typeof pose === 'number' ? pose : crossed && side ? QUARTER : ARM_ANGLE[pose] ?? 0;
  const angle = endOn ? 0 : (left ? 1 : -1) * out + (pose === 'side' || pose === 'down' ? swing : 0);
  // The pivot is the middle of the arm's top square, just outside the body (or where the side view says).
  const pivot = [at ?? (left ? L0 - w / 2 : R0 + 1 + w / 2), ar + w / 2];
  const px = [], drawn = (!left && artR) || art;
  for (let k = 0; k < n; k++) for (let j = 0; j < w; j++) {
    const tag = drawn ? limbArt(drawn, k, j, w, left) : k < sleeve ? '@arms' : '@hands';
    if (tag) px.push([j, k, tag]);
  }
  const limb = { kind: 'arm', w, n, px, pivot, ox: -w / 2, oy: -w / 2, angle, sleeve, layer: crossed || front ? 'front' : layer || 'back' };
  limb.hand = cellAt(limb, 0, n - 0.5 - w / 2);         // the middle of its last row
  return limb;
}

// Something gripped in a block hand is part of the arm, like a sword in Minecraft: its handle
// sits in the fist and it turns with the arm. Its drawing (in ITEMS) points up from the handle at
// (0, 0); in the hand it points forward from the fist (outward, seen from the front), turned on
// by `wrist` (radians, toward the front). Upright, it keeps the turn it has with the arm hanging
// down, wherever the arm goes (a spear carried upright). An item that goes in front ("front": a
// shield) is drawn over the body while the arm is lowered, and behind it once the arm is raised.
function gripLimb(arm, item, which, wrist, upright) {
  if (!item) return null;
  const art = item.px, flip = which === 'L' ? -1 : 1, px = art.map(([c, r, t]) => [flip * c, r, t]);
  const cs = px.map(q => q[0]), rs = px.map(q => q[1]), c0 = Math.min(...cs), r0 = Math.min(...rs);
  const y = arm.n - arm.w, cos = Math.cos(arm.angle), sin = Math.sin(arm.angle);   // y: the middle of the fist, along the arm
  const front = item.front && Math.abs(arm.angle) <= QUARTER + 1e-6;
  return { kind: 'item', w: Math.max(...cs) - c0 + 1, n: Math.max(...rs) - r0 + 1, px: px.map(([c, r, t]) => [c - c0, r - r0, t]),
    pivot: [arm.pivot[0] - y * sin, arm.pivot[1] + y * cos], ox: c0 - 0.5, oy: r0 - 0.5,
    angle: (upright ? 0 : arm.angle) + flip * (QUARTER + wrist), layer: front ? 'front' : arm.layer };
}

// An item by name: one of the design's own ("items": { "spear": { "art": rows of its palette
// letters, pointing up, "grip": [col, row] where the hand holds it, "front" } }, so its skins
// color it), or one from ITEMS. As { px, front }, worked out once per item.
const itemCache = new WeakMap();
export function itemArt(design, name) {
  const own = design.items?.[name];
  if (!own) return ITEMS[name] ? { px: ITEMS[name] } : null;
  if (!itemCache.has(own)) {
    const [gc, gr] = own.grip || [0, 0];
    const px = own.art.flatMap((row, r) => [...row].flatMap((ch, c) => (ch === '.' ? [] : [[c - gc, r - gr, '@' + ch]])));
    itemCache.set(own, { px, front: !!own.front });
  }
  return itemCache.get(own);
}

// Block legs hang from the hips. Walking in profile swings them back and forth with the stride
// (p.stride); the old walkA/walkB steps tilt them a little; spread turns them apart, a kick
// swings them in turn, and sitting lets them hang over the edge.
//
// Splayed legs (a spider's, "splay": { "hip": row, "reach": [...] }) come out of the body at the
// hip row and reach out and down to the ground, each as long as its reach says, its foot on the
// side the reach's sign says (- left, + right; turned, + is forward). The lower the body, the
// flatter they spread, so crouching or sitting splays them out. Walking lifts them in turn.
function legLimbs(p, { cols, bottom, leg, now, side, w, foot, footRows, splay, art, alike = false }) {
  const mode = p.legs || 'stand';
  if (mode === 'none' || (leg === 0 && !p.sit && !splay)) return [];
  const half = cols.length / 2, kick = Math.floor(now / 90) % 2, amp = side ? 0.5 : 0.12;
  return cols.map((c0, i) => {
    let angle = 0, n = p.sit ? Math.max(1, leg) : leg, pivotY = p.sit ? 0 : bottom + 1;
    if (mode === 'walk' && !splay) angle = amp * Math.sin((p.stride || 0) + (i % 2) * Math.PI);
    else if (mode === 'walkA') angle = i % 2 ? amp : -amp;
    else if (mode === 'walkB') angle = i % 2 ? -amp : amp;
    else if (mode === 'kick') angle = (i + kick) % 2 ? 0.5 : -0.5;
    else if (mode === 'spread') angle = i < half ? 0.3 : -0.3;
    if (splay) {
      const reach = splay.reach[i % splay.reach.length];
      n = Math.abs(reach); pivotY = splay.hipY;
      const lift = mode === 'walk' ? 0.45 * Math.max(0, Math.sin((p.stride || 0) + (i % 2) * Math.PI)) : 0;   // half of them up, half down
      angle += -Math.sign(reach) * (Math.acos(clamp(-pivotY / n, 0, 1)) + lift);
    }
    const px = [];
    for (let k = 0; k < n; k++) for (let j = 0; j < w; j++) {
      const tag = art ? limbArt(art, k, j, w, !alike && i >= half) : k >= n - footRows ? foot : '@legs';   // (alike: none mirrored)
      if (tag) px.push([j, k, tag]);
    }
    return { kind: 'leg', w, n, px, pivot: [c0 + w / 2, pivotY], ox: -w / 2, oy: 0, angle, footRows, layer: 'back' };
  });
}

// A tail ("tail" in the side view: { "at": [col, row], "art": [...], "angle" }) is a block limb
// behind the body, drawn from its texture (rows from the root out, "." for none) and turning at
// its root: the middle of its first square sits at [col, row] of the side drawing, and it points
// "angle" degrees from hanging down toward the back. pose.tail turns it further (radians: a wag),
// walking makes it bob a little, and leaning its back on a wall (the back arm against it, the
// other crossed) it keeps it down, out of the wall. A tail in two pieces, like a cat's, has a
// "tip": { "art", "angle" } that goes on from the end of the first piece, turned "angle" degrees
// further. Returns the pieces.
function tailLimb(tail, p, L0, top) {
  const piece = (art, pivot, angle) => {
    const w = art[0].length, n = art.length, px = [];
    for (let k = 0; k < n; k++) for (let j = 0; j < w; j++) if (art[k][j] !== '.') px.push([j, k, '@' + art[k][j]]);
    return { kind: 'tail', w, n, px, pivot, ox: -w / 2, oy: -w / 2, angle, layer: 'back' };
  };
  const bob = p.legs === 'walk' ? 0.12 * Math.sin(2 * (p.stride || 0)) : 0;
  const lean = p.armL === 'none' && p.armR === 'crossed' ? -Math.PI / 2 : 0;
  const root = piece(tail.art, [L0 + tail.at[0], top + tail.at[1]], (tail.angle ?? 90) * Math.PI / 180 + (p.tail || 0) + bob + lean);
  if (!tail.tip) return [root];
  // The tip starts on the root piece's last square, so the bend has no gap.
  const reach = root.n - root.w, end = [root.pivot[0] - reach * Math.sin(root.angle), root.pivot[1] + reach * Math.cos(root.angle)];
  return [root, piece(tail.tip.art, end, root.angle + (tail.tip.angle ?? 0) * Math.PI / 180)];
}

// A block limb drawn from its texture ("art" in "arms" or "legs": rows of palette letters from
// the top down, as wide as the limb; "artR" for a right arm of its own, like the Iron Golem's
// vines): the letter at row k, column j. The other side's limb is the mirror image. A "." is
// nothing (null): the gap in a crab's open claw.
function limbArt(rows, k, j, w, mirrored) {
  const row = rows[Math.min(k, rows.length - 1)], ch = row[mirrored ? w - 1 - j : j];
  return ch === '.' ? null : '@' + ch;
}

// The pixel cell at a point given relative to the limb's pivot before turning.
function cellAt(limb, x, y) {
  const cos = Math.cos(limb.angle), sin = Math.sin(limb.angle);
  return [Math.floor(limb.pivot[0] + x * cos - y * sin + 1e-9), Math.floor(limb.pivot[1] + x * sin + y * cos + 1e-9)];
}
// Every pixel of a limb at a right angle, on the grid; null at other angles.
function exactCells(limb) {
  const q = limb.angle / QUARTER;
  if (Math.abs(q - Math.round(q)) > 1e-6) return null;
  return approxCells(limb);
}
function approxCells(limb) {
  return limb.px.map(([j, k, tag]) => [...cellAt(limb, j + 0.5 + limb.ox, k + 0.5 + limb.oy), tag]);
}
// Turned to face left: the limb flips around the body's middle and turns the other way.
function mirrorLimb(limb) {
  return { ...limb, pivot: [-limb.pivot[0], limb.pivot[1]], ox: -limb.ox - limb.w, angle: -limb.angle, px: limb.px.map(([j, k, t]) => [limb.w - 1 - j, k, t]) };
}

// The head, turned at the neck by p.nod (0..1): every pixel in the head's areas (with whatever
// sits on it) and its hat leaves the body and becomes one block that turns around the joint and
// drops.
function headLimb(out, hatPx, neck, nod, L0, top) {
  const inHead = q => hatPx.has(q) || neck.areas.some(([c0, c1, r1]) => q[0] >= L0 + c0 && q[0] <= L0 + c1 && q[1] <= top + r1);
  const head = out.filter(inHead);
  if (!head.length) return null;
  const rest = out.filter(q => !inHead(q));
  out.length = 0; out.push(...rest);
  const cs = head.map(q => q[0]), rs = head.map(q => q[1]), c0 = Math.min(...cs), r0 = Math.min(...rs);
  const jx = L0 + neck.pivot[0], jy = top + neck.pivot[1];
  return { kind: 'head', w: Math.max(...cs) - c0 + 1, n: Math.max(...rs) - r0 + 1, px: head.map(([c, r, t]) => [c - c0, r - r0, t]),
    pivot: [jx, jy + nod * neck.drop], ox: c0 - jx, oy: r0 - jy, angle: nod * neck.turn, layer: 'front' };
}

// ---------- packs ----------
function jetpack(p, now, { L0, top, bottom, put }) {
  // A pack flush against its back, as tall as the body, with two nozzles underneath.
  const rows = bottom - top + 1;
  for (let i = 0; i < rows; i++) {
    const shade = i === 0 ? [METAL.hi, METAL.mid, METAL.mid] : i === rows - 1 ? [METAL.low, METAL.low, METAL.low] : [METAL.mid, METAL.mid, METAL.dark];
    shade.forEach((col, j) => put(L0 - 3 + j, top + i, col));
  }
  put(L0, top + 1, METAL.strap); put(L0, bottom, METAL.strap);
  put(L0 - 3, bottom + 1, METAL.nozzle); put(L0 - 1, bottom + 1, METAL.nozzle);
  if (p.flame > 0) {
    const t = Math.floor(now / 70);
    for (const c of [L0 - 3, L0 - 1]) {
      const len = clamp(p.flame - (hash(c, 7, t) < 0.5 ? 1 : 0), p.flame > 1 ? 1 : 0, 3);
      for (let k = 0; k < len; k++) put(c, bottom + 2 + k, FLAME[k]);
    }
  }
}

function elytraOpen(L0, top, put) {
  // Long wing cases from the shoulders, swept back to a point.
  const art = [
    '........VVVV',
    '....VVVWWWWW',
    '.VVWWWWWWWw.',
    'WWWWwwwww...',
    'ww..........',
  ];
  art.forEach((line, i) => [...line].forEach((ch, j) => { if (ch !== '.') put(L0 - 9 + j, top - 2 + i, WING[ch]); }));
}

// The first jetpack, kept unchanged so it can be compared with the new one. Its tanks bend
// when it tilts, because only the top two rows lean.
function legacyJetpack(p, now) {
  const px = [];
  for (let r = 0; r < 4; r++) for (let c = 3; c <= 14; c++) px.push([c, r, '@body']);
  const EYES = { center: [0, 1], left: [-1, 1], right: [1, 1], up: [0, 0] };
  if (p.blink) px.push([5, 1, 'C'], [12, 1, 'C']);
  else { const [dx, row] = EYES[p.eyes] || EYES.center; px.push([5 + dx, row, 'E'], [12 + dx, row, 'E']); }
  for (const x0 of [1, 15]) for (let r = 0; r <= 3; r++) {
    px.push([x0, r, r === 0 ? '#B5BDC6' : r === 3 ? '#6B737C' : '#C9D1D9']);
    px.push([x0 + 1, r, r === 3 ? '#5C636B' : '#9AA3AD']);
  }
  const old = p.tilt ? [p.tilt, p.tilt, 0, 0] : null;
  if (old) for (const q of px) if (q[1] <= 3) q[0] += old[clamp(q[1], 0, 3)] || 0;
  if (p.flying) [4, 6, 11, 13].forEach((c, i) => { px.push([c, 4, '@body']); if (Math.sin(now / 520 + i * 1.9) < 0.55) px.push([c, 5, '@body']); });
  else for (const c of [4, 6, 11, 13]) px.push([c, 4, '@body']);
  for (const c of [1, 2, 15, 16]) px.push([c, 4, '#4A5058']);
  if (p.flame > 0) {
    const t = Math.floor(now / 70);
    for (const c of [1, 2, 15, 16]) {
      const len = clamp(p.flame - (hash(c, 7, t) < 0.5 ? 1 : 0), p.flame > 1 ? 1 : 0, 3);
      for (let k = 0; k < len; k++) px.push([c, 5 + k, FLAME[k]]);
    }
  }
  const mapped = px.map(([c, r, t]) => [c - 9, r - 5, t]);
  return { px: mapped, hand: { L: [-8, -3], R: [7, -3] }, top: -5, bottom: -2, L0: -6, R0: 5, hatTop: -5 };
}
